import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Box, Container } from '@mui/material';
import { Loader, Button, Typography } from '@sm-dashboard/ui-components';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { verifyPaymentUrl } from '@/constants';
import instanceAxios from '@/util/axios';

// TODO: Currently assumes the system Cardcom provider (redirect-finalized flow).
// When this page starts supporting non-system / webhook-flow providers (e.g. Grow),
// adopt the branching pattern from ShaliachPaymentTestPage / PaymentStep:
// detect webhook-flow providers and use `usePaymentStatusPolling` against
// `GET /pub/payment/status/:sessionId` instead of the one-shot verify call.
const BusinessAdPaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { adId } = useParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        if (!adId) {
          setError('לא ניתן למצוא מזהה מודעה');
          return;
        }

        const sessionId =
          searchParams.get('lowprofilecode') ||
          searchParams.get('processId') ||
          searchParams.get('session_id') ||
          searchParams.get('sessionId');
        if (!sessionId) {
          setError('לא ניתן למצוא טרנזקציה');
          return;
        }

        const response = await instanceAxios.get(`${verifyPaymentUrl}/${sessionId}`);
        if (response.data?.normalized && response.data.normalized.success === false) {
          setError(response.data.normalized.failureReason || 'שגיאה באימות התשלום');
        }
        setVerifying(false);
      } catch (err) {
        console.error('Error verifying payment:', err);
        setError('שגיאה באימות התשלום');
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams, adId]);

  if (verifying) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Loader />
        <Box sx={{ mt: 2 }}>
          <Typography variant="h3">אנו בודקים את התשלום שלך...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Box sx={{ mb: '0.35em' }}>
          <Typography variant="h3" color="error.main">
            {error}
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/business-ads/submit/publisher')}>
            חזרה לטופס הפרסום
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
      <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
      <Box sx={{ mb: '0.35em' }}>
        <Typography variant="h3">התשלום בוצע בהצלחה!</Typography>
      </Box>
      <Box sx={{ mb: '0.35em' }}>
        <Typography variant="body2" color="text.secondary">
          התשלום בוצע בהצלחה! המודעה שלך נשמרה וממתינה לאישור מנהל המערכת.
        </Typography>
      </Box>
      <Box sx={{ mb: '0.35em' }}>
        <Typography variant="body2" color="text.secondary">
          תקבל עדכון במייל כאשר המודעה תאושר ותפורסם.
        </Typography>
      </Box>
      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button variant="outlined" onClick={() => navigate('/business-ads/submit/publisher')}>
          פרסם מודעה נוספת
        </Button>
      </Box>
    </Container>
  );
};

export default BusinessAdPaymentSuccess;
